import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ComposerScrollbar.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ComposerScrollbarProps {
  /**
   * Where the thumb starts, in px, measured from **the top of the track** — not from the top of
   * the card.
   *
   * The artboard draws both bars at `top: 14px`, i.e. the thumb flush against the track's own top,
   * which is `thumbTop={0}` here. Track-relative is the only version a caller can compute:
   * `scrollTop / scrollHeight * 154`.
   */
  thumbTop: number;
  /**
   * Thumb length in px. The artboard draws 46 in a 154px track — a little under a third, which is
   * the ratio of the 174px viewport to the ~590px of text in it.
   */
  thumbHeight: number;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The composer's own scrollbar, for when the text row has hit its 174px ceiling.
 *
 * One artboard draws it — `Chat/Long text` — and it is the only use of `--radius-2xs` and
 * of the light-25 step in the whole Chat column. It exists because a native overlay scrollbar
 * inside a 400px card is either invisible (macOS, until you scroll) or 15px of grey furniture
 * (Windows), and neither is the drawing.
 *
 * **It positions itself absolutely**, so the `ComposerCard` around it must be `position:
 * relative` — the file adds exactly that to this one card, alongside the two cards that anchor a
 * popover.
 *
 * The whole thing is `aria-hidden`: it is a picture of an overflow that the real scroll container
 * underneath already reports to assistive tech, and it takes no pointer events, so announcing it
 * would be announcing a second, fake scrollbar.
 */
export function ComposerScrollbar({
  thumbTop,
  thumbHeight,
  theme: themeProp,
  className,
}: ComposerScrollbarProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div aria-hidden="true" className={cn(chatThemeClass(theme), styles.track, className)}>
      <div
        className={styles.thumb}
        // Both are runtime values — the only thing inline `style` is for here.
        style={
          {
            '--cv15-scroll-thumb-top': `${thumbTop}px`,
            '--cv15-scroll-thumb-h': `${thumbHeight}px`,
          } as React.CSSProperties
        }
      />
    </div>
  );
}
